import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  Rocket,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Edit,
  Trash2,
  AlertTriangle,
  X,
  Plus,
} from "lucide-react";
import RoomCreateModal from "./RoomCreateModal";
import { toast } from "react-toastify";
import {
  getMeetingRooms,
  createMeetingRoom,
  deleteMeetingRoom,
  updateMeetingRoom,
} from "../../../../services/roomServices";
import { useSiteContext } from "../../../../context/SiteContext";
const Rooms = () => {
  const { user } = useSiteContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rooms, setRooms] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRooms, setTotalRooms] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getRooms();
  }, [currentPage, rowsPerPage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest("[data-dropdown]")) {
        console.log("Clicking outside, closing dropdown");
        setShowDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const getRoomType = (roomType) => {
    return String(roomType).toLowerCase() === "conferencing"
      ? "Meeting"
      : roomType;
  };

  const getRooms = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getMeetingRooms(currentPage, rowsPerPage);
      setRooms(response.data || []);
      setTotalRooms(response.totalOwnerRooms || 0);
      setTotalPages(response.pagination.totalPages || 1);
    } catch (err) {
      console.log(err);
      setError("Error fetching rooms");
      setRooms([]);
      setTotalRooms(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  // const handleCopyRoom = (roomId) => {
  //   // Handle copy room functionality
  //   console.log("Copy room:", roomId);
  //   navigator.clipboard.writeText(`${window.location.origin}/${roomId}`);
  //   toast.success("Room link copied to clipboard");
  // };

  // const convertRoomHtmlToText = (html) => {
  //   const container = document.createElement("div");
  //   container.innerHTML = html;
  
  //   const getText = (label) => {
  //     const el = Array.from(container.querySelectorAll("p")).find(p =>
  //       p.textContent?.includes(label)
  //     );
  //     return el?.textContent?.replace(label, "").trim() || "N/A";
  //   };
  
  //   const meetingLinkEl = container.querySelector("a");
  //   const meetingLink = meetingLinkEl?.getAttribute("href") || "N/A";
  
  //   return `
  //     Meeting Starting Soon!
      
  //     ${container.querySelector("p")?.textContent || ""}
      
  //     Topic: ${getText("Meeting Name:")}
  //     Meeting Room: ${getText("Meeting Room:")}
  //     Date & Time: ${getText("Date & Time:")}
      
  //     Join Meeting
  //     ${meetingLink}
      
  //     Meeting Passcode: ${getText("Meeting Passcode:")}
  //     Voice Dial-in: ${getText("Voice Dial-in:")}
  //     Voice Room ID: ${getText("Voice Room ID:")}
  //     `.trim();
  // };

  const convertRoomHtmlToText = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
  
    // Replace <br> and block tags with new lines
    doc.querySelectorAll("br").forEach(br => br.replaceWith("\n"));
  
    doc.querySelectorAll("p, div, h1, h2, h3").forEach(el => {
      el.append("\n");
    });
  
    // Convert links to raw URLs
    doc.querySelectorAll("a").forEach(a => {
      const url = a.getAttribute("href");
      a.replaceWith(url ? `${url}\n` : "");
    });
  
    let text = doc.body.textContent || "";
  
    // Clean formatting
    return text
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]+/g, " ")
      .trim();
  };
  
  

  const handleCopyRoom = async (linkTemplate) => {
    const plainText = convertRoomHtmlToText(linkTemplate);
    await navigator.clipboard.writeText(plainText);
    toast.success("Room invitation copied to clipboard");
  };

  const handleLaunchRoom = (room) => {
    // Handle launch room functionality
    window.open(`${window.location.origin}/${room.roomName}`, "_blank");
  };

  const handleMoreOptions = (roomId, event) => {
    event.stopPropagation();
    console.log(
      "Current showDropdown:",
      showDropdown,
      "Clicked roomId:",
      roomId
    );
    setShowDropdown(showDropdown === roomId ? null : roomId);
  };

  const handleEditRoom = (room) => {
    // setEditingRoom(room);
    // setShowEditModal(true);
    setShowDropdown(null);
    navigate(`/dashboard/edit-room/${room.id}`);
  };

  const handleDeleteRoom = (room) => {
    setRoomToDelete(room);
    setShowDeleteModal(true);
    setShowDropdown(null);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await deleteMeetingRoom(roomToDelete.id);
      getRooms(); // Refresh the rooms list
      setShowDeleteModal(false);
      setRoomToDelete(null);
      toast.success("Room deleted successfully");
    } catch (err) {
      console.error("Error deleting room:", err);
      toast.error("Error deleting room. Please try again.");
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteModal(false);
    setRoomToDelete(null);
  };

  const handleUpdateRoom = (roomData) => {
    console.log("Updating room with data:", roomData);
    updateMeetingRoom({
      ...roomData,
      _id: editingRoom._id,
      room: editingRoom.room,
    })
      .then((response) => {
        console.log(response);
        getRooms();
        setShowEditModal(false);
        setEditingRoom(null);
      })
      .catch((err) => {
        console.error("Error updating room:", err);
        alert("Error updating room. Please try again.");
      });
  };

  const handleCreateRoom = (roomData) => {
    console.log("Creating room with data:", roomData);
    // Add your API call here to create the room
    // After successful creation, refresh the rooms list
    createMeetingRoom({
      ...roomData,
    }).then((response) => {
      console.log(response);
      getRooms();
      setShowCreateModal(false);
    });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen text-white p-3 md:p-6 flex items-center justify-center"
        style={{ backgroundColor: "#565167" }}
      >
        <div className="text-base md:text-xl">Loading rooms...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 md:gap-0">
          <h1 className="text-xl md:text-3xl font-bold text-white uppercase">
            Rooms
          </h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            {user &&
              ((user.plan == "Business" && totalRooms < 5) ||
                (user.plan == "Basic" && totalRooms < 1)) && (
                <button
                  onClick={() => navigate("/dashboard/add-new-room")}
                  className="px-3 md:px-4 py-2 bg-[#7700FF] text-white rounded-lg transition-colors font-semibold hover:bg-[#6600DD] flex items-center justify-center space-x-2 text-sm md:text-base"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Room</span>
                </button>
              )}
            <div className="text-xs md:text-sm text-gray-300">
              Total: {totalRooms} rooms
            </div>
          </div>
        </div>

        {/* Rooms List */}
        <div
          className="rounded-xl border border-gray-600 overflow-hidden primary-card-background"
          // style={{ backgroundColor: "#3C3A4C" }}
        >
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-700">
            <h2 className="text-base md:text-lg font-semibold text-white uppercase">
              ROOMS
            </h2>
          </div>

          <div className="p-3 md:p-6">
            {error && (
              <div className="text-red-400 text-center py-4 text-sm md:text-base">
                {error}
              </div>
            )}

            {rooms.length === 0 && !loading && !error && (
              <div className="text-gray-400 text-center py-6 md:py-8 text-sm md:text-base">
                No rooms found
              </div>
            )}

            {rooms.map((room) => (
              <div
                key={room.id}
                className="flex items-center justify-between py-3 md:py-4 border-b border-gray-700 last:border-b-0 gap-2 md:gap-0"
              >
                <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-base md:text-lg">
                      {room.roomName?.charAt(0) || "R"}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-medium text-sm md:text-base truncate">
                      {room.roomName}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm truncate">
                      {getRoomType(room.roomType) || "Meeting"} (
                      {room.roomCreatedBy == user.id ? "Owner" : "Co-host"})
                    </p>
                    <p className="text-gray-400 text-xs md:text-sm truncate">
                      Voice Room ID: {room.voiceRoomId}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-1 md:space-x-2 flex-shrink-0">
                  <button
                    // onClick={() => handleCopyRoom(room.roomName)}
                    onClick={() => handleCopyRoom(room.copyLinkTemplate)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Copy room"
                  >
                    <Copy className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handleLaunchRoom(room)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Launch room"
                  >
                    <Rocket className="h-4 w-4 text-green-400" />
                  </button>
                  <div className="relative" data-dropdown>
                    <button
                      onClick={(e) => handleMoreOptions(room.roomName, e)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      title="More options"
                    >
                      <MoreVertical className="h-4 w-4 text-white" />
                    </button>

                    {showDropdown === room.roomName && (
                      <div
                        className="absolute right-0 top-full mt-1 w-48 bg-[#2A2835] border border-gray-600 rounded-lg shadow-xl z-50"
                        style={{
                          boxShadow:
                            "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                        }}
                      >
                        <div className="py-1">
                          <button
                            onClick={() => handleEditRoom(room)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center"
                          >
                            <Edit className="h-4 w-4 mr-3" />
                            Edit Room
                          </button>
                          {room.roomCreatedBy === user.id && (
                            <button
                              onClick={() => handleDeleteRoom(room)}
                              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300 transition-colors flex items-center"
                            >
                              <Trash2 className="h-4 w-4 mr-3" />
                              Delete Room
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-0">
              <div className="flex items-center space-x-2 text-xs md:text-sm text-white">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) =>
                    handleRowsPerPageChange(Number(e.target.value))
                  }
                  className="bg-gray-700 border border-gray-600 rounded-md px-2 py-1 text-white text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-gray-500 transition-colors"
                >
                  <option value={10} className="bg-gray-700 text-white">
                    10
                  </option>
                  <option value={25} className="bg-gray-700 text-white">
                    25
                  </option>
                  <option value={50} className="bg-gray-700 text-white">
                    50
                  </option>
                </select>
              </div>
              <div className="flex items-center space-x-2 md:space-x-4">
                <span className="text-xs md:text-sm text-white">
                  {currentPage} of {totalPages}
                </span>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronsLeft className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4 text-white" />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronsRight className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Room Create Modal */}
      <RoomCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateRoom={handleCreateRoom}
      />

      {/* Room Edit Modal */}
      <RoomCreateModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingRoom(null);
        }}
        onCreateRoom={handleUpdateRoom}
        initialData={editingRoom}
        isEdit={true}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={cancelDeleteRoom}
          ></div>

          {/* Modal Content */}
          <div className="relative bg-[#060621] rounded-lg shadow-xl w-full max-w-md mx-4 border border-gray-600">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-600">
              <h3 className="text-lg font-semibold text-white">Delete Room</h3>
              <button
                onClick={cancelDeleteRoom}
                className="text-gray-400 hover:text-white transition-colors touch-manipulation p-1"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="mb-4">
                <p className="text-gray-300 text-sm mb-4">
                  Are you sure you want to delete the room{" "}
                  <span className="font-semibold text-white">
                    "{roomToDelete?.roomName}"
                  </span>
                  ?
                </p>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-red-300 font-medium text-sm mb-1">
                        Warning
                      </h4>
                      <p className="text-red-200 text-xs">
                        This will permanently delete the room and all its data.
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-600">
                <button
                  onClick={cancelDeleteRoom}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteRoom}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rooms;
